'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntersection } from 'react-use';

import cx from 'clsx';
import { m, useMotionValue, useSpring } from 'framer-motion';

import ButtonCTA from 'components/ui/ButtonCTA';
import Img from 'components/ui/Img';
import Portal from 'components/ui/Portal';
import Text from 'components/ui/Text';

import useMediaQuery from 'hooks/useMediaQuery';

import OurJourneyNav from './OurJourneyNav';

import EyebrowRibbon from '../EyebrowRibbon';
import TalesOverlay from '../TalesOverlay';

import styles from './OurJourney.module.scss';

const OurJourney = ({ overlayChapters, customProperties }) => {
    const rootRef = useRef(null);
    const cardsRef = useRef(null);
    const cardsSectionRef = useRef(null);
    const previousWidthRef = useRef(0);
    const [trackWidth, setTrackWidth] = useState(0);
    const [itemWidth, setItemWidth] = useState(0);
    const [activeNavId, setActiveNavId] = useState('chapter-1');
    const [activeChapterId, setActiveChapterId] = useState(null);
    const [cardsInView, setCardsInView] = useState(false);
    const [inViewState, setInViewState] = useState({
        'chapter-1': true,
    });
    const [keyboardActive, setKeyboardActive] = useState(false);

    const isMedium = useMediaQuery('(min-width: 768px)');

    const x = useMotionValue(0);
    const springX = useSpring(x, { stiffness: 400, damping: 30 });

    const memoRef = useRef(0);
    const isDraggingRef = useRef(false);
    const startXRef = useRef(0);
    const animationFrameRef = useRef(null);
    const velocityRef = useRef(0);
    const startTimeRef = useRef(0);
    const clickedSlideId = useRef(0);

    const minX = itemWidth - trackWidth;
    const maxX = 0;

    const itemGap = isMedium ? 10 : 12;

    const intersection = useIntersection(cardsSectionRef, {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
    });

    useEffect(() => {
        intersection && intersection.isIntersecting
            ? setCardsInView(true)
            : null;
    }, [intersection]);

    const keyboardIntersection = useIntersection(rootRef, {
        root: null,
        rootMargin: '0px',
        threshold: 0,
    });

    useEffect(() => {
        keyboardIntersection &&
            setKeyboardActive(keyboardIntersection.isIntersecting);
    }, [keyboardIntersection]);

    const updateDimensions = useCallback(() => {
        if (rootRef.current && cardsRef.current) {
            const trackWidth = cardsRef.current.clientWidth;
            const itemWidth = cardsRef.current.children[0].clientWidth;

            setTrackWidth(trackWidth);
            setItemWidth(itemWidth);
        }
    }, []);

    useEffect(() => {
        updateDimensions();

        window.addEventListener('resize', updateDimensions);

        return () => {
            window.removeEventListener('resize', updateDimensions);
        };
    }, [updateDimensions]);

    useEffect(() => {
        const handleReset = () => {
            // quick fix, reset index to 0 on width resize
            const currentWidth = rootRef.current.clientWidth;
            if (currentWidth !== previousWidthRef.current) {
                previousWidthRef.current = currentWidth;
                setActiveNavId('chapter-1');
                x.set(0);
            }
        };

        window.addEventListener('resize', handleReset);

        return () => {
            window.removeEventListener('resize', handleReset);
        };
    }, [x]);

    const handlePointerMove = event => {
        if (!isDraggingRef.current) return;
        const isTouch = event.type === 'touchmove';
        const eventX = isTouch ? event.touches[0].clientX : event.clientX;
        const mx = eventX - startXRef.current;
        const newX = memoRef.current + mx;

        const resistanceFactor = 2; // rubberband resistance

        let boundedX = newX;

        // rubberband effect
        if (newX < minX) {
            boundedX = minX + (newX - minX) / resistanceFactor;
        } else if (newX > maxX) {
            boundedX = maxX + (newX - maxX) / resistanceFactor;
        }

        x.set(boundedX);
    };

    const handlePointerUp = event => {
        if (!isDraggingRef.current) return;

        isDraggingRef.current = false;

        const isTouch = event.type === 'touchend';
        const eventX = isTouch
            ? event.changedTouches[0].clientX
            : event.clientX;
        const mx = eventX - startXRef.current;
        const timeElapsed = performance.now() - startTimeRef.current;
        const velocity = mx / timeElapsed;

        velocityRef.current = velocity * 10;

        // if movement is less than 5px, consider it a click
        if (Math.abs(mx) < 5) {
            handleNavClick(clickedSlideId.current);
            return;
        }

        const movementThreshold = 70;
        const movementExceeds = Math.abs(mx) > movementThreshold;

        const changeIndexThreshold = 20;
        const velocityExceeds =
            Math.abs(velocityRef.current) > changeIndexThreshold;
        const shouldChangeIndex = velocityExceeds || movementExceeds;

        if (shouldChangeIndex) {
            const currentX = x.get();
            const currentIndex = Math.round(-currentX / itemWidth);
            const nextIndex =
                velocityRef.current > 0 ? currentIndex - 1 : currentIndex + 1;

            let targetX = -nextIndex * (itemWidth + itemGap);
            targetX = Math.max(Math.min(targetX, maxX), minX);

            const boundedNextIndex = Math.max(
                0,
                Math.min(nextIndex, overlayChapters.length - 1)
            );

            const id = overlayChapters[boundedNextIndex].id;
            setActiveNavId(id);
            setInViewState(prevState => ({
                ...prevState,
                [id]: true,
            }));

            x.set(targetX);
        } else {
            x.set(memoRef.current);
        }
    };

    const handlePointerDown = event => {
        isDraggingRef.current = true;
        const isTouch = event.type === 'touchstart';
        const eventX = isTouch ? event.touches[0].clientX : event.clientX;
        startXRef.current = eventX;
        memoRef.current = x.get();
        startTimeRef.current = performance.now();
        cancelAnimationFrame(animationFrameRef.current);

        // save clicked slide id in case of click
        const target = event.target.closest('[data-id]');
        if (target) {
            const id = target.getAttribute('data-id');
            clickedSlideId.current = id;
        }
    };

    const handleOverlayClose = () => {
        setActiveChapterId(null);
    };

    const handleCardClick = id => {
        // open chapter overlay
        setActiveChapterId(id);
    };

    const handleNavClick = useCallback(
        id => {
            setActiveNavId(id);

            const targetIndex = overlayChapters.findIndex(
                chapter => chapter.id === id
            );

            if (targetIndex !== -1) {
                const targetX = -targetIndex * (itemWidth + itemGap);

                const minX = itemWidth - trackWidth;
                const maxX = 0;
                const boundedTargetX = Math.max(Math.min(targetX, maxX), minX);

                x.set(boundedTargetX);

                setInViewState(prevState => ({
                    ...prevState,
                    [id]: true,
                }));
            }
        },
        [overlayChapters, itemWidth, itemGap, trackWidth, x]
    );

    useEffect(() => {
        if (!keyboardActive) return;

        const handleKeyDown = event => {
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                const currentIndex = overlayChapters.findIndex(
                    chapter => chapter.id === activeNavId
                );

                let newIndex;
                if (event.key === 'ArrowLeft') {
                    newIndex = Math.max(currentIndex - 1, 0);
                } else if (event.key === 'ArrowRight') {
                    newIndex = Math.min(
                        currentIndex + 1,
                        overlayChapters.length - 1
                    );
                }

                const newId = overlayChapters[newIndex].id;
                handleNavClick(newId);
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeNavId, overlayChapters, keyboardActive, handleNavClick]);

    return (
        <>
            <section
                ref={rootRef}
                id="journey"
                className={cx(styles.root, {
                    [styles.cardsInView]: cardsInView,
                })}
                onMouseLeave={handlePointerUp}
            >
                <div className={styles.sticky}>
                    <div className={styles.header}>
                        <div className={styles.lockup}>
                            <Text
                                className={styles.headline}
                                baseTheme="displayMedium"
                                themes={{
                                    medium: 'displayLarge',
                                    large: 'displayXLarge',
                                }}
                            >
                                Our Journey
                            </Text>
                            <Text
                                className={styles.copy}
                                baseTheme="bodyMedium"
                                themes={{ large: 'bodyLarge' }}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: `We’ve been farmer-owned since forever. Well, since 1909. So “forever-ish.” Since then, every challenge and triumph has shaped us into who we are today. Explore the chapters below for our full story.`,
                                    }}
                                />
                            </Text>
                        </div>
                    </div>
                    <div ref={cardsSectionRef} className={styles.cardsScroll}>
                        <div className={styles.cardsContainer}>
                            <div
                                className={styles.cardsContainerInner}
                                onMouseDown={handlePointerDown}
                                onMouseMove={handlePointerMove}
                                onMouseUp={handlePointerUp}
                                onTouchStart={handlePointerDown}
                                onTouchMove={handlePointerMove}
                                onTouchEnd={handlePointerUp}
                                onTouchCancel={handlePointerUp}
                            >
                                <m.ul
                                    ref={cardsRef}
                                    className={styles.cards}
                                    style={{
                                        cursor: 'grab',
                                        x: springX,
                                    }}
                                >
                                    {overlayChapters.map((chapter, index) => (
                                        <li
                                            key={index}
                                            className={cx(styles.card, {
                                                [styles.isInView]:
                                                    inViewState[chapter.id],
                                            })}
                                            style={{
                                                backgroundColor: `var(--tales-color-${chapter.color})`,
                                                '--image-origin':
                                                    chapter.imageConfig?.origin,
                                                '--image-transform': `translate(${chapter.imageConfig?.translateX}, ${chapter.imageConfig?.translateY})`,
                                            }}
                                            data-id={chapter.id}
                                        >
                                            <div className={styles.cardInner}>
                                                <span
                                                    className={
                                                        styles.cardLockup
                                                    }
                                                >
                                                    <EyebrowRibbon
                                                        isXLarge
                                                        className={
                                                            styles.cardEyebrow
                                                        }
                                                    >
                                                        {chapter.eyebrow}
                                                    </EyebrowRibbon>
                                                    <Text
                                                        className={
                                                            styles.cardTitle
                                                        }
                                                        baseTheme="displayXXSmall"
                                                    >
                                                        <span
                                                            dangerouslySetInnerHTML={{
                                                                __html: chapter.title,
                                                            }}
                                                        />
                                                    </Text>
                                                    <ButtonCTA
                                                        className={
                                                            styles.cardCta
                                                        }
                                                        style="filled-blue-ocean"
                                                        text="Explore"
                                                        onClick={() => {
                                                            handleCardClick(
                                                                chapter.id
                                                            );
                                                        }}
                                                        gtm-label={`our-journey-overlay-cta-${chapter.id}`}
                                                    />
                                                </span>
                                                <Img
                                                    className={cx(
                                                        styles.cardImage,
                                                        styles[chapter.id]
                                                    )}
                                                    src={chapter.cardImage.url}
                                                    width={
                                                        chapter.cardImage?.width
                                                    }
                                                    height={
                                                        chapter.cardImage
                                                            ?.height
                                                    }
                                                    alt={
                                                        chapter.cardImage
                                                            .description
                                                    }
                                                    customSources={[
                                                        {
                                                            breakpoint: 768,
                                                            src: chapter
                                                                .cardImageLarge
                                                                .url,
                                                            imageWidth: 1280,
                                                        },
                                                        {
                                                            src: chapter
                                                                .cardImage.url,
                                                            imageWidth: 500,
                                                        },
                                                    ]}
                                                />
                                            </div>
                                        </li>
                                    ))}
                                </m.ul>
                            </div>
                        </div>
                    </div>
                    <OurJourneyNav
                        overlayChapters={overlayChapters}
                        activeNavId={activeNavId}
                        onNavClick={handleNavClick}
                        x={springX}
                        carouselDistance={trackWidth - itemWidth}
                    />
                </div>
            </section>
            <Portal>
                <div style={customProperties}>
                    {overlayChapters.map(chapter => {
                        return (
                            <TalesOverlay
                                key={chapter.id}
                                id={chapter.id}
                                content={chapter}
                                isActive={activeChapterId === chapter.id}
                                onClose={handleOverlayClose}
                                overlayChapters={overlayChapters}
                                onSetChapterId={id => {
                                    setActiveChapterId(id);
                                }}
                                onActiveChapterSelect={handleNavClick}
                            />
                        );
                    })}
                </div>
            </Portal>
        </>
    );
};

export default OurJourney;
